import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable, catchError, finalize, forkJoin, map, tap, filter, EMPTY } from 'rxjs';
import { IFile, IFileHydrated, IAPIListResponse, IListOptions, IWebsocketMessage } from '@newroom-connect/library/interfaces';
import { ArrayHelper, ImageHelper, FileHelper } from '@newroom-connect/library/helpers';
import { FileType } from '@newroom-connect/library/enums';
import { FileProcessingStatus, IFileProcessingProgress } from '@newroom-connect/library/types';

import { EntityService } from '../entity/entity.service';
import { LoggingService } from '../logging/logging.service';

import { FileProcessingService } from './file-processing.service';

export interface IUpdateFileTranslationInput {
  code: string;
  title?: string;
  filenameDownload?: string;
}

export interface IUpdateFileInput {
  tags?: string[];
  translations?: IUpdateFileTranslationInput[];
}

export enum FileGatewayAction {
  TRANSLATE_DOCUMENT = 'TRANSLATE_DOCUMENT',
  CUBEMAP_GENERATION = 'CUBEMAP_GENERATION',
  THUMBNAIL_GENERATION = 'THUMBNAIL_GENERATION',
  PDF_EXPORT = 'PDF_EXPORT'
}

export interface UploadProgress {
  status: 'UPLOADING' | 'UPLOADED' | 'FAILED';
  processed?: number;
  total?: number;
  filename?: string;
}

export interface IFileGatewayJobDetails {
  id?: string;
  status?: string;
  from?: string;
  to?: string;
  projectId: string;
  file?: IFileHydrated,
  fileId?: string;
}

export interface IFileGatewayData {
  action: FileGatewayAction;
  job: IFileGatewayJobDetails,
  file?: IFileHydrated,
}

@Injectable({
  providedIn: 'root'
})
export class FileService extends EntityService {
  public static readonly SUPPORTED_TRANSLATION_MIMETYPES = [
    'text/plain',
    'text/html',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/x-xliff+xml'
  ];

  public readonly logger = inject(LoggingService);
  private readonly httpClient = inject(HttpClient);
  private readonly fileProcessingService = inject(FileProcessingService);

  public searchableProperties = ['mimetype', 'translations[].title'];

  public uploadProgress = new Map<string, UploadProgress>();

  /**
   * Hydrate the provided file with more information.
   *
   * @param file The file to hydrate with more information.
   *
   * @returns The object of the hydrated file.
   */
  public static hydrateFile(file: IFile): IFileHydrated {
    const fileExtension = file.filenameDisk.match(/\.[0-9a-z]+$/i);

    // Determine if the file is supported for cubemap generation.
    const isCubemapSupported = !!(file.width && file.height && ImageHelper.isCubeMapSupported({ width: file.width, height: file.height }));
    const isCubemapInProgress = file.queueJobs && ArrayHelper.isNotEmpty(file.queueJobs) && file.queueJobs.some(
      queueJob => queueJob.name === 'panorama-to-cubemap' && queueJob.status === 'ACTIVE'
    );

    // Determine if the file is supported for translation.
    const isTranslationSupported = FileService.SUPPORTED_TRANSLATION_MIMETYPES.includes(file.mimetype);
    const isTranslationJobInProgress = file.queueJobs && ArrayHelper.isNotEmpty(file.queueJobs) && file.queueJobs.some(
      queueJob => queueJob.name === 'translate' && queueJob.status === 'ACTIVE'
    );

    const thumbnailFormat = file.formats?.find(fileFormat => fileFormat.type === FileType.THUMBNAIL);

    return {
      ...file,
      source: FileHelper.getFileSourceURI(FileService.baseUrl, file),
      thumbnail: thumbnailFormat ? FileHelper.getFileSourceURI(FileService.baseUrl, thumbnailFormat) : undefined,
      extension: fileExtension ? fileExtension[0].slice(1).toUpperCase() : '',
      title: file.translations[0]?.title ?? file.filenameDisk,
      cubemapStatus: FileHelper.isFileCubemap(file) ? 'generated' : (isCubemapInProgress ? 'in-progress' : (isCubemapSupported ? 'supported' : undefined)),
      isCubemapModalVisible: false,
      translationStatus: isTranslationSupported ? (isTranslationJobInProgress ? 'in-progress' : 'supported') : undefined,
      isTranslationModalVisible: false
    };
  }

  /**
   *
   * @param projectId
   * @param options
   *
   * @returns
   */
  public listFiles(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IFileHydrated>> {
    return super.list<IFile>(`projects/${projectId}/files`, undefined, options).pipe(
      map(response => {
        response.data = response.data.map(file => FileService.hydrateFile(file));
        return response as IAPIListResponse<IFileHydrated>;
      })
    );
  }

  /**
   *
   * @param projectId
   * @param options
   *
   * @returns
   */
  public listSourceFiles(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IFileHydrated>> {
    const requestOptions: IListOptions = options ?? {};

    if (!requestOptions.filters) {
      requestOptions.filters = {};
    }

    if (!requestOptions.filters['AND']) {
      requestOptions.filters['AND'] = [];
    }

    // If the source filter is not present, add it to the list of filters.
    if (!requestOptions.filters['AND'].some((filter: { type: string }) => filter.type === 'SOURCE')) {
      requestOptions.filters['AND'].push(
        { type: { equals: 'SOURCE' } }
      );
    }

    return this.listFiles(projectId, requestOptions);
  }

  /**
   *
   * @param projectId
   * @param fileId
   *
   * @returns
   */
  public getFile(projectId: string, fileId: string): Observable<IFileHydrated> {
    return this.apiService.get<IFile>(`projects/${projectId}/files/${fileId}`).pipe(
      map(file => FileService.hydrateFile(file))
    );
  }

  /**
   *
   * @param projectId
   * @param fileId
   * @param input
   *
   * @returns
   */
  public updateFile(projectId: string, fileId: string, input: IUpdateFileInput): Observable<IFileHydrated> {
    return this.apiService.patch<IFile>(`projects/${projectId}/files/${fileId}`, input).pipe(
      map(file => FileService.hydrateFile(file))
    );
  }

  /**
   *
   * @param projectId
   * @param fileIds
   *
   * @returns
   */
  public deleteFiles(projectId: string, fileIds: string[]): Observable<IFile[]> {
    return forkJoin(fileIds.map(fileId => this.apiService.delete<IFile>(`projects/${projectId}/files/${fileId}`)));
  }

  /**
   *
   * @param projectId
   * @param fileId
   *
   * @returns
   */
  public convertFile(projectId: string, fileId: string): Observable<void> {
    return this.apiService.post<void>(`projects/${projectId}/files/${fileId}/convert`);
  }

  /**
   *
   * @param projectId
   * @param fileId
   *
   * @returns
   */
  public exportFileAsPdf(projectId: string, fileId: string): Observable<{ sessionId: string, success: boolean, status: string }> {
    return this.apiService.post<{ sessionId: string, success: boolean, status: string }>(`projects/${projectId}/files/${fileId}/export-pdf`);
  }

  /**
   *
   * @param projectId
   *
   * @returns
   */
  public watchFiles(projectId: string): Observable<IWebsocketMessage<IFileGatewayData>> {
    return this.websocketService.watchTopic<IFileGatewayData>(`projects/${projectId}/files`).pipe(
      map(message => {
        if (!message.data.file) {
          return message;
        }

        // If a file is present in the gateway message, hydrate it so it includes all the extra properties needed.
        message.data.file = FileService.hydrateFile(message.data.file);

        return message;
      })
    );
  }

  /**
   *
   * @param sourceFileUrl
   * @returns
   */
  public getFileContent(sourceFileUrl: string): Observable<string> {
    return this.httpClient.get<string>(`${sourceFileUrl}?time=${new Date().getTime()}`, { responseType: 'text' as 'json' });
  }

  /**
   * Get all unique file tags for a project.
   *
   * @param projectId
   *
   * @returns Observable of string array containing unique tags.
   */
  public getProjectFileTags(projectId: string): Observable<string[]> {
    return this.apiService.get<string[]>(`projects/${projectId}/files/tags?ngsw-bypass=true`);
  }

  /**
   *
   * @param projectId
   * @param fileId
   *
   * @returns
   */
  public deleteFile(projectId: string, fileId: string): Observable<IFile> {
    return this.apiService.delete<IFile>(`projects/${projectId}/files/${fileId}`);
  }

  /**
   * Downloads files and handles progress tracking including SSE for zip creation.
   *
   * @param projectId
   * @param fileIds
   * @param sessionId
   * @param filename
   * @returns Observable of download events.
   *
   * @throws
   */
  public downloadFiles(projectId: string, fileIds: string[], sessionId: string | null, filename?: string): Observable<HttpResponse<Blob>> {
    if (!sessionId) {
      throw new Error('Unable to refresh session: No user session available');
    }

    // Use consistent ID that matches SSE progress tracking
    const downloadId = `${projectId}-download-${sessionId}`;

    // Start SSE progress tracking for multiple files
    if (fileIds.length > 1) {
      this.fileProcessingService.watchProgress(projectId, 'download', sessionId)
        .pipe(
          catchError(error => {
            this.logger.error('Download progress error:', error);
            return EMPTY;
          })
        )
        .subscribe();
    } else {
      // For single file download, set initial progress manually
      const initialProgress: IFileProcessingProgress = {
        id: downloadId,
        status: FileProcessingStatus.DOWNLOADING,
        total: 100,
        processed: 0,
        filename,
        operation: 'download',
        projectId
      };

      // Update progress through the FileProcessingService
      this.fileProcessingService.updateProgress(initialProgress);
    }

    const headers: Record<string, string> = fileIds.length > 1 ? { 'Accept': 'application/zip' } : {};

    return this.apiService.post<HttpEvent<Blob>>(
      `projects/${projectId}/files/download`,
      { fileIds, sessionId },
      {
        responseType: 'blob',
        observe: 'events',
        reportProgress: true,
        headers: new HttpHeaders(headers)
      }
    ).pipe(
      tap(response => {
        if (!response) {
          return;
        }

        const lastProgress = this.fileProcessingService.processingProgress.value?.find(progress => progress.id === downloadId);

        // Handle progress events for single file downloads
        if ((fileIds.length === 1 || lastProgress?.status === FileProcessingStatus.DOWNLOADING) && response.type === HttpEventType.DownloadProgress) {
          const progress: IFileProcessingProgress = {
            id: downloadId,
            status: FileProcessingStatus.DOWNLOADING,
            processed: response.loaded,
            total: response.total,
            filename,
            operation: 'download',
            projectId
          };

          // Update progress through the FileProcessingService
          this.fileProcessingService.updateProgress(progress);
        }

        if (response.type !== HttpEventType.Response || !response.body) {
          return;
        }

        const downloadedFileName = this.processDownloadResponse(response as HttpResponse<Blob>);

        // Update to completed status
        const completedProgress: IFileProcessingProgress = {
          id: downloadId,
          status: FileProcessingStatus.COMPLETED,
          filename: downloadedFileName,
          operation: 'download',
          projectId
        };

        // Update progress through the FileProcessingService
        this.fileProcessingService.updateProgress(completedProgress);

        // Stop tracking and remove after 10 seconds
        setTimeout(() => {
          this.fileProcessingService.removeProgress(downloadId);
        }, 10000);
      }),
      finalize(() => {
        // No need to manually disconnect SSE anymore
      }),
      catchError(() => {
        const errorProgress: IFileProcessingProgress = {
          id: downloadId,
          status: FileProcessingStatus.FAILED,
          operation: 'download',
          projectId
        };

        // Update progress through the FileProcessingService
        this.fileProcessingService.updateProgress(errorProgress);

        // Remove after 5 seconds
        setTimeout(() => {
          this.fileProcessingService.removeProgress(downloadId);
        }, 5000);
        return EMPTY;
      }),
      // Filter to only return Response events
      filter((event: HttpEvent<Blob>): event is HttpResponse<Blob> => event.type === HttpEventType.Response)
    );
  }

  /**
   *
   * @param file
   *
   * @returns
   */
  public static isFileTranslationSupported(file: IFile): boolean {
    return FileService.SUPPORTED_TRANSLATION_MIMETYPES.includes(file.mimetype);
  }

  /**
   *
   * @param event
   *
   * @returns
   * @throws
   */
  private processDownloadResponse(event: HttpResponse<Blob>): string {
    const blob = event.body;
    const contentDisposition = event.headers.get('content-disposition');
    const contentType = event.headers.get('content-type');

    if (!blob || !contentDisposition || !contentType) {
      throw new Error('Invalid download response');
    }

    const fileName = FileHelper.extractFilenameFromContentDispositionHeader(contentDisposition);

    FileHelper.downloadFile(blob, fileName, contentType);

    return fileName;
  }
}
